// Artifact math and the stat source that applies them.
import { D, Decimal, fmt, ZERO } from '../core/decimal';
import { ARTIFACTS, ARTIFACT_DISCOVERY, type ArtifactDef } from '../data/artifacts';
import { BALANCE } from '../data/balance';
import type { AddStat, ModifierSource } from './stats';

const EXACT_SUM_LIMIT = 24;

/** Relic cost of levels [level, level + count): sum of ceil(costBase * (i + 1) ^ costPower). */
export function artifactCost(def: ArtifactDef, level: number, count = 1): Decimal {
  if (count <= EXACT_SUM_LIMIT) {
    let sum = ZERO;
    for (let i = level; i < level + count; i++) sum = sum.add(D(i + 1).pow(def.costPower).mul(def.costBase).ceil());
    return sum;
  }
  // Midpoint-rule integral of c·x^p over [level + 0.5, level + count + 0.5]: within a fraction of a
  // percent of the exact sum for large counts, and O(1) regardless of how many levels are bought.
  const p1 = def.costPower + 1;
  const a = D(level + 0.5).pow(p1), b = D(level + count + 0.5).pow(p1);
  return b.sub(a).mul(def.costBase / p1).ceil();
}

/** How many levels `relics` buys (at least 0, exact: never over-spends). */
export function artifactAffordable(def: ArtifactDef, level: number, relics: Decimal): number {
  if (artifactCost(def, level, 1).gt(relics)) return 0;
  const p1 = def.costPower + 1;
  // Invert the integral for an estimate, then correct against the real cost function.
  const top = relics.mul(p1 / def.costBase).add(D(level + 0.5).pow(p1));
  let n = Math.max(1, Math.floor(Math.pow(10, top.log10() / p1) - 0.5 - level));
  while (n > 1 && artifactCost(def, level, n).gt(relics)) n = Math.max(1, Math.floor(n * 0.98));
  while (artifactCost(def, level, n + 1).lte(relics)) n++;
  return n;
}

export function discoveryCost(owned: number): Decimal {
  return D(ARTIFACT_DISCOVERY.base).mul(Decimal.pow(ARTIFACT_DISCOVERY.growth, owned)).ceil();
}

/** Current value of an artifact's primary effect. */
export function effectValue(def: ArtifactDef, level: number): Decimal {
  const e = def.effect;
  if (e.kind === 'mult') return D(level).pow(e.levelExp ?? 1).mul(e.perLevel).add(1);
  if (e.kind === 'div') return D(1).div(D(e.perLevel).mul(level).add(1));
  return D(Math.min(e.cap, e.perLevel * level));
}

export function riderValue(def: ArtifactDef, level: number): Decimal {
  return D(def.damagePerLevel).mul(level).add(1).pow(BALANCE.artifactRiderPower);
}

const ADD_FORMAT: Record<AddStat, (v: number) => string> = {
  critChance: (v) => `+${(v * 100).toFixed(1)}%`,
  tapDpsShare: (v) => `+${(v * 100).toFixed(1)}%`,
  offlineEfficiency: (v) => `+${Math.round(v * 100)}%`,
  manaRegenPct: (v) => `+${Math.round(v * 100)}%`,
  skillDurationPct: (v) => `+${Math.round(v * 100)}%`,
  bossTimeSec: (v) => `+${v.toFixed(1)}s`,
  warcryBonus: (v) => `+x${v.toFixed(1)}`,
  frenzyRate: (v) => `+${v.toFixed(2)}`,
  maxMana: (v) => `+${Math.round(v)}`,
  fireswordBonus: (v) => `+x${v.toFixed(1)}`,
};

export function describeEffect(def: ArtifactDef, level: number): string {
  const e = def.effect;
  const v = effectValue(def, level);
  const text = e.kind === 'add' ? ADD_FORMAT[e.stat](v.toNumber()) : 'x' + (v.lt(1000) ? v.toNumber().toFixed(2) : fmt(v));
  const capped = e.kind === 'add' && e.perLevel * level >= e.cap ? ' (max)' : '';
  return def.desc.replace('{v}', text + capped);
}

/** Registers every owned artifact's primary effect and all-damage rider. */
export function artifactSource(): ModifierSource {
  return {
    id: 'artifacts',
    apply(state, m) {
      for (const def of ARTIFACTS) {
        const level = state.artifacts[def.id] ?? 0;
        if (level <= 0) continue;
        const e = def.effect;
        if (e.kind === 'add') m.add(e.stat, effectValue(def, level).toNumber());
        else m.mult(e.stat, effectValue(def, level));
        if (def.damagePerLevel > 0) m.mult('allDamage', riderValue(def, level));
      }
    },
  };
}
